import React, { Suspense, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Panel, Tab, Col, Row, Nav, NavItem, NavDropdown, MenuItem } from 'react-bootstrap';
import { navigate } from 'raviger';
import _ from 'lodash';

import withAuthentication from '../../hoc/withAuthentication';
import withLayout from '../../hoc/withLayout';

import { useAlertStore } from '../../store/providers/Alert';
import { useFranchiseStore } from '../../store/providers/Franchise';
import { useTransactionDocumentLookupsStore } from '../../store/providers/TransactionDocumentLookups';

import { breadcrumbAddRoute, breadcrumbRemoveRoute } from '../../store/actions/breadcrumb';
import { franchiseSelect } from '../../store/actions/franchise';

import Spinner from '../../components/Spinner/Spinner';

import * as constants from '../../constants';
import './Franchise.css';
import IntegrationsForm from './IntegrationsForm';

const SideMenu = React.lazy(() => import('../../components/SideMenu/SideMenu'));
const PageHeader = React.lazy(() => import('../../components/PageHeader/PageHeader'));
const OfficeTab = React.lazy(() => import('../../components/Tabs/OfficeTab/OfficeTab'));
const CommissionPlanTemplateTab = React.lazy(() => import('../../components/Tabs/CommissionPlanTemplateTab/CommissionPlanTemplateTab'));
const CommissionPlanTemplateWaitingPeriodTab = React.lazy(() => import('../../components/Tabs/CommissionPlanTemplateWaitingPeriodTab/CommissionPlanTemplateWaitingPeriodTab'));
const PromotionTab = React.lazy(() => import('../../components/Tabs/PromotionTab/PromotionTab'));
const FranchiseProductTab = React.lazy(() => import('../../components/Tabs/FranchiseProductTab/FranchiseProductTab'));
const FranchiseAgentProductTab = React.lazy(() => import('../../components/Tabs/FranchiseAgentProductTab/FranchiseAgentProductTab'));
const FranchiseMlsProviderTab = React.lazy(() => import('../../components/Tabs/FranchiseMlsProviderTab/FranchiseMlsProviderTab'));
const TerritoryTab = React.lazy(() => import('../../components/Tabs/TerritoryTab/TerritoryTab'));
const NoteTab = React.lazy(() => import('../../components/Tabs/NoteTab/NoteTab'));
const RealEstateBoardTab = React.lazy(() => import('../../components/Tabs/RealEstateBoardTab/RealEstateBoardTab'));
const FranchiseUserTab = React.lazy(() => import('../../components/Tabs/FranchiseUserTab/FranchiseUserTab'));
const FranchiseFileTab = React.lazy(() => import('../../components/Tabs/FranchiseFileTab/FranchiseFileTab'));
const FranchiseRoyaltyPlanTab = React.lazy(() => import('../../components/Tabs/FranchiseRoyaltyPlanTab/FranchiseRoyaltyPlanTab'));
const FranchiseAccountingTab = React.lazy(() => import('../../components/Tabs/FranchiseAccountingTab/FranchiseAccountingTab'));
const LedgerTab = React.lazy(() => import('./Tabs/LedgerTab'));
const DisclaimerTab = React.lazy(() => import('../Franchises/Tabs/FranchiseDisclaimerTab/FranchiseDisclaimerTab'));
const DirtyModal = React.lazy(() => import('../../components/Modals/DirtyModal/DirtyModal'));
const FranchiseForm = React.lazy(() => import('./FranchiseForm'));
const DocumentSetupTab = React.lazy(() => import('../../components/Tabs/DocumentSetupTab/DocumentSetupTab'));
const TransactionFeeTab = React.lazy(() => import('../../components/Tabs/TransactionFeeTab/TransactionFeeTab'));
const AgentFeeTab = React.lazy(() => import('../../components/Tabs/AgentFeeTab/AgentFeeTab'));
const RSASettingsTab = React.lazy(() => import('../../components/Tabs/RSASettingsTab/RSASettingsTab'));
const FranchiseScoreCardReportTab = React.lazy(() => import('../../components/Tabs/FranchiseScoreCardReportTab/FranchiseScoreCardReportTab'));

function Franchise(props) {
    const dispatch = useDispatch();

    const { addAlert } = useAlertStore();
    const { selectRecord, newRecord, data, clearData } = useFranchiseStore();
    const { franchiseTabKey: selectedTab, setFranchiseTabKey: setSelectedFranchiseTab, setFranchiseInstance } = useTransactionDocumentLookupsStore();

    const [isMounted, setIsMounted] = useState(false);
    const [isNew, setIsNew] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [isBusy, setIsBusy] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);
    const [title, setTitle] = useState('');
    const [showDirtyModal, setShowDirtyModal] = useState(false);
    const [isNetSuiteIntegrationEnabled, setIsNetSuiteIntegrationEnabled] = useState(false);
    const [isGoogleGroupsEnabled, setIsGoogleGroupsEnabled] = useState(false);
    const [franchiseObj, setFranchiseObj] = useState(false);

    const noDirtyClicked = () => {
        setShowDirtyModal(false);
        this.setState({ showDirtyModal: false, selectedTab: this.state.selectedTab });
    };

    const yesDirtyClicked = () => {
        setShowDirtyModal(false);
    };

    const cancelledHandler = () => {
        navigate('/instances');
    };

    const submittedHandler = () => {
        navigate('/instances');
    };

    const tabSelectedHandler = (tabKey) => {
        if (isDirty && tabKey !== selectedTab) {
            setIsDirty(true);
        } else {
            if (tabKey !== constants.FRANCHISE_FORM_BUILDR_TAB_KEY) {
                setSelectedFranchiseTab(tabKey);
            } else {
                navigate(`/formBuilder/${props.franchiseId}`);
            }
        }
    };

    useEffect(() => {
        if (!_.isEmpty(data)) {
            if (data.status === 'fail' || data.status === 'error') {
                addAlert({ key: constants.ALERT_SERVER_ERROR, text: <strong>{data.body}</strong>, type: constants.ALERT_TYPE_DANGER });
                clearData();
            }

            if (!isLoaded && isMounted && data.scope === 'select' && data.status === 'success') {
                setTitle(`Edit Instance (ID: ${data.body.id} - Instance: ${data.body.instance})`);
                setIsNetSuiteIntegrationEnabled(data.body.features.some(f => f.id === constants.FEATURE_FLAG_ENUM.NetSuiteIntegration))
                setIsGoogleGroupsEnabled(data.body.features.some(f => f.id === constants.FEATURE_FLAG_ENUM.GoogleGroupManagement))
                setIsLoaded(true);
                setIsNew(false);
                setFranchiseInstance(data.body.instance);
                setFranchiseObj(data.body);
                dispatch(breadcrumbAddRoute({
                    key: 'edit-franchise',
                    text: data.body.instance,
                    path: {
                        event: () => {
                            setSelectedFranchiseTab(constants.DEFAULT_FRANCHISE_TAB);
                            navigate(`/instances/edit/${props.franchiseId}`);
                        }
                    }
                }));
            }

            if (!isNew && (data.scope === 'new' && data.status === 'success')) {
                setTitle(`New Instance`);
                setIsLoaded(true);
                setIsNew(true);
                dispatch(breadcrumbAddRoute({
                    key: 'new-franchise',
                    text: 'New Instance',
                    path: {
                        event: () => setSelectedFranchiseTab(constants.DEFAULT_FRANCHISE_TAB)
                    }
                }));
            }

            setIsBusy(data.status === 'processing' && (data.scope === 'update' || data.scope === 'create'));
        }
    }, [props, data, addAlert, clearData, isLoaded, isMounted, isNew, dispatch, setSelectedFranchiseTab, setFranchiseInstance]);

    useEffect(() => {
        if (isMounted) { return; }


        dispatch(breadcrumbAddRoute({
            key: 'instances', text: 'Instances', path: {
                event: () => {
                    dispatch(breadcrumbRemoveRoute(2));
                    navigate('/instances');
                }
            }
        }));

        if (!_.isEmpty(props.franchiseId)) {
            dispatch(franchiseSelect({ franchiseId: props.franchiseId }));
            selectRecord({ franchiseId: props.franchiseId });
        } else {
            newRecord();
            dispatch(franchiseSelect());
        }

        setIsMounted(true);
    }, [isMounted, dispatch, newRecord, props.franchiseId, selectRecord]);

    useEffect(() => {
        return () => {
            dispatch(breadcrumbRemoveRoute());
        };
    }, [dispatch]);

    return (
        <Suspense fallback={<Spinner text="Loading Instance..." />}>
            {
                <>
                    {
                        showDirtyModal && (
                            <DirtyModal show={showDirtyModal}
                                noClick={noDirtyClicked}
                                yesClick={yesDirtyClicked} />
                        )
                    }
                    <SideMenu activeKey="franchises" />
                    <div className="content-body">
                        <div style={{ paddingRight: '15px' }}>
                            <PageHeader title={title} />
                            <Panel>
                                <Panel.Body>
                                    <Tab.Container id='editFranchiseTabContainer' defaultActiveKey="general" activeKey={selectedTab} onSelect={tabSelectedHandler}>
                                        <Row className="clearfix">
                                            <Col sm={12}>
                                                <Nav bsStyle="pills">
                                                    <NavItem eventKey="general" disabled={isBusy}>General</NavItem>
                                                    <NavItem eventKey="office" disabled={isBusy || isNew}>Branch Offices</NavItem>
                                                    <NavDropdown eventKey="commission" title="Commissions" disabled={isBusy || isNew}>
                                                        <MenuItem className="tabMenuItem" eventKey="commission_plan" disabled={isBusy || isNew}>Plans</MenuItem>
                                                        <MenuItem className="tabMenuItem" eventKey="waiting_period" disabled={isBusy || isNew}>Plan Rules</MenuItem>
                                                        <MenuItem className="tabMenuItem" eventKey="promotion" disabled={isBusy || isNew}>Promotions</MenuItem>
                                                        <MenuItem className="tabMenuItem" eventKey="transaction_fee" disabled={isBusy || isNew}>Transaction Fees</MenuItem>
                                                    </NavDropdown>
                                                    <NavItem eventKey="agent_fees" disabled={isBusy || isNew}>Agent Fees</NavItem>
                                                    <NavItem eventKey="royalty_plan" disabled={isBusy || isNew}>Royalty Plans</NavItem>
                                                    {
                                                        isNetSuiteIntegrationEnabled && <NavItem eventKey="accounting" disabled={isBusy || isNew}>Accounting</NavItem>
                                                    }
                                                    <NavDropdown eventKey="realEstate" title="Subscriptions" disabled={isBusy || isNew}>
                                                        <NavItem eventKey="franchiseAgentProduct" disabled={isBusy || isNew}>Agent Subscriptions</NavItem>
                                                        <NavItem eventKey="franchiseProduct" disabled={isBusy || isNew}>Instance Subscriptions</NavItem>
                                                    </NavDropdown>
                                                    <NavDropdown eventKey="realEstate" title="Real Estate" disabled={isBusy || isNew}>
                                                        <NavItem className="tabMenuItem" eventKey="real_estate_board" disabled={isBusy || isNew}>Memberships</NavItem>
                                                        <NavItem className="tabMenuItem" eventKey="mls_provider" disabled={isBusy || isNew}>MLS</NavItem>
                                                        <NavItem className="tabMenuItem" eventKey="document_setup" disabled={isBusy || isNew}>Document Setup</NavItem>
                                                    </NavDropdown>
                                                    <NavDropdown eventKey="support" title="Support" disabled={isBusy || isNew}>
                                                        <NavItem className="tabMenuItem" eventKey="ledger" disabled={isBusy || isNew}>Ledger</NavItem>
                                                        <NavItem className="tabMenuItem" eventKey="territory" disabled={isBusy || isNew}>Territories</NavItem>
                                                        <NavItem className="tabMenuItem" eventKey="staff" disabled={isBusy || isNew}>Staff</NavItem>
                                                        <NavItem className="tabMenuItem" eventKey="file" disabled={isBusy || isNew}>Files</NavItem>
                                                        <NavItem className="tabMenuItem" eventKey="disclaimer" disabled={isBusy || isNew}>Disclaimers</NavItem>
                                                        <NavItem className="tabMenuItem" eventKey="note" disabled={isBusy || isNew}>Notes</NavItem>
                                                    </NavDropdown>
                                                    <NavItem eventKey="rsaSettings" disabled={isBusy}>RSA Settings</NavItem>
                                                    {
                                                        isGoogleGroupsEnabled && <NavItem eventKey="integrations" disabled={isBusy || isNew}>Integrations</NavItem>
                                                    }
                                                    <NavItem eventKey="rsaScoreCard" disabled={isBusy}>Scorecard</NavItem>
                                                </Nav>
                                            </Col>
                                            <Col sm={12}>
                                                <Tab.Content animation>
                                                    <Tab.Pane eventKey="general">
                                                        <FranchiseForm
                                                            isNew={isNew}
                                                            cancelled={() => cancelledHandler()}
                                                            submitted={() => submittedHandler()} />
                                                    </Tab.Pane>
                                                    <Tab.Pane eventKey="office">
                                                        {
                                                            (!isBusy && selectedTab === 'office') && <OfficeTab compType="franchise" />
                                                        }
                                                    </Tab.Pane>
                                                    <Tab.Pane eventKey="commission_plan">
                                                        {
                                                            (!isBusy && selectedTab === "commission_plan") && <CommissionPlanTemplateTab compType="franchise" />
                                                        }
                                                    </Tab.Pane>
                                                    <Tab.Pane eventKey="waiting_period">
                                                        {
                                                            (!isBusy && selectedTab === "waiting_period") && <CommissionPlanTemplateWaitingPeriodTab franchiseId={props.franchiseId} />
                                                        }
                                                    </Tab.Pane>
                                                    <Tab.Pane eventKey="promotion">
                                                        {
                                                            (!isBusy && selectedTab === "promotion") && <PromotionTab />
                                                        }
                                                    </Tab.Pane>
                                                    <Tab.Pane eventKey="agent_fees">
                                                        {
                                                            (!isBusy && selectedTab === "agent_fees") && <AgentFeeTab compType="franchise" {...props} />
                                                        }
                                                    </Tab.Pane>
                                                    <Tab.Pane eventKey="royalty_plan">
                                                        {
                                                            (!isBusy && selectedTab === "royalty_plan") && <FranchiseRoyaltyPlanTab />
                                                        }
                                                    </Tab.Pane>
                                                    <Tab.Pane eventKey="accounting">
                                                        {
                                                            (!isBusy && selectedTab === "accounting") && <FranchiseAccountingTab />
                                                        }
                                                    </Tab.Pane>
                                                    <Tab.Pane eventKey="franchiseAgentProduct">
                                                        {
                                                            (!isBusy && selectedTab === "franchiseAgentProduct") && <FranchiseAgentProductTab />
                                                        }
                                                    </Tab.Pane>
                                                    <Tab.Pane eventKey="franchiseProduct">
                                                        {
                                                            (!isBusy && selectedTab === "franchiseProduct") && <FranchiseProductTab />
                                                        }
                                                    </Tab.Pane>
                                                    <Tab.Pane eventKey="real_estate_board">
                                                        {
                                                            (!isBusy && selectedTab === "real_estate_board") && <RealEstateBoardTab compType="franchise" />
                                                        }
                                                    </Tab.Pane>
                                                    <Tab.Pane eventKey="mls_provider">
                                                        {
                                                            (!isBusy && selectedTab === "mls_provider") && <FranchiseMlsProviderTab />
                                                        }
                                                    </Tab.Pane>
                                                    <Tab.Pane eventKey="document_setup">
                                                        {
                                                            (!isBusy && selectedTab === "document_setup") && <DocumentSetupTab compType="DocumentSetup" {...props} />
                                                        }
                                                    </Tab.Pane>
                                                    <Tab.Pane eventKey="form-builder">
                                                        <div></div>
                                                    </Tab.Pane>
                                                    <Tab.Pane eventKey="ledger">
                                                        {
                                                            (!isBusy && selectedTab === "ledger") && <LedgerTab />
                                                        }
                                                    </Tab.Pane>
                                                    <Tab.Pane eventKey="territory">
                                                        {
                                                            (!isBusy && franchiseObj && selectedTab === 'territory') && <TerritoryTab franchise={franchiseObj} compType="franchise" />
                                                        }
                                                    </Tab.Pane>
                                                    <Tab.Pane eventKey="staff">
                                                        {
                                                            (!isBusy && selectedTab === "staff") && <FranchiseUserTab />
                                                        }
                                                    </Tab.Pane>
                                                    <Tab.Pane eventKey="file">
                                                        {
                                                            (!isBusy && selectedTab === "file") && <FranchiseFileTab compType="franchise" title={`Upload File for ${data.body.instance}`} postUrl={`/File/Franchise/${data.body.id}`} />
                                                        }
                                                    </Tab.Pane>
                                                    <Tab.Pane eventKey="disclaimer">
                                                        {
                                                            (!isBusy && selectedTab === "disclaimer") && <DisclaimerTab />
                                                        }
                                                    </Tab.Pane>
                                                    <Tab.Pane eventKey="note">
                                                        {
                                                            (!isBusy && selectedTab === "note") && <NoteTab compType="franchise" parentTab="Support" clearBreadcrumb />
                                                        }
                                                    </Tab.Pane>
                                                    <Tab.Pane eventKey="transaction_fee">
                                                        {
                                                            (!isBusy && selectedTab === "transaction_fee") && <TransactionFeeTab compType="franchise" {...props} />
                                                        }
                                                    </Tab.Pane>
                                                    <Tab.Pane eventKey="rsaSettings">
                                                        {
                                                            (!isBusy && selectedTab === "rsaSettings") && <RSASettingsTab compType="franchise" {...props} />
                                                        }
                                                    </Tab.Pane>
                                                    {
                                                        isGoogleGroupsEnabled &&
                                                        <Tab.Pane eventKey="integrations">
                                                            {
                                                                (!isBusy && selectedTab === "integrations") && <IntegrationsForm />
                                                            }
                                                        </Tab.Pane>
                                                    }
                                                    <Tab.Pane eventKey="rsaScoreCard">
                                                        {
                                                            (!isBusy && selectedTab === "rsaScoreCard") && <FranchiseScoreCardReportTab compType="franchise" />
                                                        }
                                                    </Tab.Pane>
                                                </Tab.Content>
                                            </Col>
                                        </Row>
                                    </Tab.Container>
                                </Panel.Body>
                            </Panel>
                        </div>
                    </div>
                </>
            }
        </Suspense>
    );
}

export default withAuthentication(withLayout(Franchise));
